import { AxiosError } from "axios";
import apiClient from "./api-client";

/** Upload files to any endpoint via multipart/form-data. Files can be File or Blob. Returns response.data. */
export const uploadFiles = async <T = unknown>(
  endpoint: string,
  files: (File | Blob)[],
  options?: { fileKey?: string }
): Promise<T> => {
  const formData = new FormData();
  const key = options?.fileKey ?? "files";
  files.forEach((file) => formData.append(key, file instanceof File ? file : file, file instanceof File ? file.name : "file"));
  const response = await apiClient.post<T>(endpoint, formData);
  return response.data;
};

export const fetchData = async (endpoint: string) => {
  try {
    const response = await apiClient.get(endpoint);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw error;
  }
};

export const post = async (url: string, data?: unknown) => {
  try {
    const response = await apiClient.post(url, data);
    return response;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw error;
  }
};

export const put = (url: string, data: unknown) => {
  return apiClient.put(url, data);
};

export const del = (url: string, data?: unknown) => {
  return apiClient.delete(url, { data });
};

export const patch = async (url: string, data: unknown) => {
  try {
    const response = await apiClient.patch(url, data);
    return response;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw error;
  }
};



export const fetchCsv = async (
  endpoint: string
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const response = await apiClient.get(endpoint, {
      responseType: "blob",
    });
    // Try to get filename from Content-Disposition
    let filename = "Persons.csv";
    const disposition = response.headers["content-disposition"];
    if (disposition) {
      const match = disposition.match(/filename=([^;]+)/);
      if (match && match[1]) {
        filename = match[1].replace(/['\"]/g, "").trim();
      }
    }
    return { blob: response.data, filename };
  } catch (error) {
    throw new Error(`Error fetching CSV: ${error}`);
  }
};

// Abort stream if no data is received for 5 minutes
const STREAM_IDLE_TIMEOUT_MS = 5 * 60 * 1000;

const getFriendlyStatusMessage = (status: number): string => {
  switch (status) {
    case 400: return "Bad request. Please check your input and try again.";
    case 401: return "Session expired. Please log in again.";
    case 403: return "You don't have permission to perform this action.";
    case 404: return "We couldn't find what you were looking for. It may have been deleted or moved.";
    case 408: return "The request timed out. Please try again.";
    case 409: return "This action conflicts with the current state. Please refresh and try again.";
    case 413: return "The file or message is too large. Please try something smaller.";
    case 422: return "Some of the information provided isn't valid. Please review and try again.";
    case 429: return "You're sending requests too quickly. Please wait a moment and try again.";
    case 500: return "Something went wrong on our end. Please try again.";
    case 502:
    case 503:
    case 504: return "The server is temporarily unavailable. Please try again in a moment.";
    default:
      return status >= 500
        ? "Something went wrong on our end. Please try again."
        : "We couldn't complete your request. Please try again.";
  }
};

const buildErrorFromResponse = async (response: Response): Promise<Error> => {
  let serverMessage: string | null = null;
  try {
    const text = await response.text();
    if (text) {
      try {
        const json = JSON.parse(text);
        const candidate = json?.message ?? json?.detail ?? json?.error;
        if (typeof candidate === "string" && candidate.trim()) {
          serverMessage = candidate.trim();
        } else if (Array.isArray(json?.detail) && typeof json.detail[0]?.msg === "string") {
          serverMessage = json.detail[0].msg;
        }
      } catch {
        if (text.trim() && text.length < 300) serverMessage = text.trim();
      }
    }
  } catch {
    // body not readable — fall through to status-based message
  }
  return new Error(serverMessage || getFriendlyStatusMessage(response.status));
};

const doFetch = (fullUrl: string, data: unknown, signal: AbortSignal) =>
  fetch(fullUrl, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

const doFetchGet = (fullUrl: string, signal: AbortSignal) =>
  fetch(fullUrl, {
    method: "GET",
    signal,
  });

const readStreamResponse = async (
  response: Response,
  onChunk: (chunk: string) => void,
  resetIdleTimer: () => void
): Promise<void> => {
  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body");
  const decoder = new TextDecoder();
  let lineBuffer = "";
  resetIdleTimer();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    resetIdleTimer();
    lineBuffer += decoder.decode(value, { stream: true });
    const lines = lineBuffer.split("\n");
    lineBuffer = lines.pop() ?? "";
    for (const line of lines) {
      if (line.startsWith("data:")) {
        const content = line.replace(/^data: ?/, "");
        onChunk(content === "" ? "\n" : content + "\n");
      } else {
        onChunk(line === "" ? "\n" : line + "\n");
      }
    }
  }
  if (lineBuffer.trim()) {
    if (lineBuffer.startsWith("data:")) {
      const content = lineBuffer.replace(/^data: ?/, "");
      onChunk(content);
    } else {
      onChunk(lineBuffer);
    }
  }
};

export const getStream = async (url: string, onChunk: (chunk: string) => void, externalSignal?: AbortSignal): Promise<void> => {
  const controller = new AbortController();
  let idleTimer: ReturnType<typeof setTimeout> | null = null;

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener("abort", () => controller.abort(), { once: true });
    }
  }

  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => controller.abort("idle_timeout"), STREAM_IDLE_TIMEOUT_MS);
  };
  try {
    const response = await doFetchGet(url, controller.signal);
    if (response.status === 401) {
      localStorage.clear();
      throw new Error("Session expired. Please log in again.");
    }
    if (!response.ok) throw await buildErrorFromResponse(response);
    await readStreamResponse(response, onChunk, resetIdleTimer);
  } catch (error) {
    if (idleTimer) clearTimeout(idleTimer);
    if (error instanceof Error) {
      if (error.name === "AbortError") return;
      if (error.name === "TypeError") {
        return;
      }
      throw error;
    }
    throw new Error(`Error in streaming request: ${error}`);
  } finally {
    if (idleTimer) clearTimeout(idleTimer);
  }
};

export const postStream = async (
  url: string,
  data: unknown,
  onChunk: (chunk: string) => void,
  externalSignal?: AbortSignal
): Promise<void> => {
  const controller = new AbortController();
  let idleTimer: ReturnType<typeof setTimeout> | null = null;

  // If an external signal is provided, abort our internal controller when it fires
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener("abort", () => controller.abort(), { once: true });
    }
  }

  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      controller.abort("idle_timeout");
    }, STREAM_IDLE_TIMEOUT_MS);
  };

  try {
    const response = await doFetch(url, data, controller.signal);

    if (response.status === 401) {
      localStorage.clear();
      throw new Error("Session expired. Please log in again.");
    }

    if (!response.ok) {
      throw await buildErrorFromResponse(response);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body");
    }

    const decoder = new TextDecoder();

    // Buffer incomplete lines so large events (e.g. tool_result with big content) split across TCP chunks are not dropped
    let lineBuffer = "";

    // Start idle timer once the connection is open
    resetIdleTimer();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Reset idle timer on every chunk received
      resetIdleTimer();

      lineBuffer += decoder.decode(value, { stream: true });
      const lines = lineBuffer.split("\n");
      // Keep the last segment in buffer (may be incomplete until next chunk)
      lineBuffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line.startsWith("data:")) {
          const content = line.replace(/^data: ?/, "");
          onChunk(content === "" ? "\n" : content + "\n");
        } else {
          // Plain text: pass through so streaming shows as it arrives
          onChunk(line === "" ? "\n" : line + "\n");
        }
      }
    }

    // Flush any remaining data (last line may not end with \n)
    if (lineBuffer.trim()) {
      if (lineBuffer.startsWith("data:")) {
        const content = lineBuffer.replace(/^data: ?/, "");
        onChunk(content);
      } else {
        onChunk(lineBuffer);
      }
    }
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        // Silently resolve — stream was cancelled (session switch, stop, or idle timeout)
        return;
      }
      if (error.name === "TypeError") {
        return;
      }
      throw error;
    }
    throw new Error(`Error in streaming request: ${error}`);
  } finally {
    if (idleTimer) clearTimeout(idleTimer);
  }
};
